import React from "react";
import { NavLink, useParams } from "react-router-dom";
import { resetPassword } from "core/services/site/user";
import { cx } from "utils/join-classnames";
import { useFormValidation } from "ui/hooks/use-form-validation";
import { Button, Spinner, FormElement } from "ui/components";
import styles from "./ResetPassword.module.scss";

interface Params {
    [key: string]: string | undefined;
    token: string;
}

const ResetPassword: React.FC = () => {
    const { token } = useParams<Params>();
    const [pending, setPending] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>("");
    const [confirm, setConfirm] = React.useState<boolean>(false);

    const theForm = useFormValidation({
        password: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 8,
                maxLength: 20,
            },
        },
        passwordconfirm: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 8,
                maxLength: 20,
                isSameAsReference: "password",
            },
        },
    });

    const handleResetPassword = async () => {
        setPending(true);
        try {
            if (!theForm.formState.password.value) {
                throw Error("Missing password");
            }
            if (!token) {
                throw Error("Invalid token");
            }
            const response = await resetPassword(
                token,
                theForm.formState.password.value.toString(),
            );
            if (response.error) {
                throw Error(response.error);
            } else {
                setConfirm(true);
            }
        } catch (err) {
            setError((err as Error).message);
        }

        setPending(false);
    };

    const tokenValid = token && token.length > 20 && token.length < 45;

    return (
        <div className={styles.page}>
            <div className={styles.reset}>
                <img
                    src="../static/img/logo-spoton.svg"
                    alt="SpotOn"
                    className={styles.reset__logo}
                />
                {!tokenValid && (
                    <div className={styles.reset__heading}>Invalid token</div>
                )}
                {confirm && (
                    <>
                        <div className={styles.reset__heading}>
                            We saved your new password
                        </div>
                        <div className={styles.reset__text}>
                            Log in and start clipping
                        </div>
                        <NavLink to="/login">
                            <Button
                                type="primary"
                                theme="light"
                                disabled={pending}
                                className={cx(
                                    styles.reset__btn,
                                    styles.reset__btn__login,
                                )}
                            >
                                Proceed to login
                            </Button>
                        </NavLink>
                    </>
                )}
                {tokenValid && !confirm && (
                    <>
                        <div className={styles.reset__heading}>
                            Enter a new password
                        </div>
                        <div className={styles.reset__text}>
                            You will be clipping again before you know it
                        </div>
                        {error !== "" && (
                            <div className={styles.errormsg}>{error}</div>
                        )}
                        <div className={styles.reset__form}>
                            {pending && (
                                <span className={styles.reset__spinner}>
                                    <Spinner />
                                </span>
                            )}
                            <div>
                                <FormElement
                                    title="New password"
                                    onChange={(value) => {
                                        theForm.inputChangeHandler(
                                            "password",
                                            value,
                                        );
                                    }}
                                    defaultText="Password"
                                    theme="dark"
                                    type="password"
                                    id="password"
                                    fieldState={theForm.formState.password}
                                />
                                <FormElement
                                    title="Confirm new password"
                                    onChange={(value) => {
                                        theForm.inputChangeHandler(
                                            "passwordconfirm",
                                            value,
                                        );
                                    }}
                                    defaultText="Confirm password"
                                    theme="dark"
                                    type="password"
                                    id="passwordconfirm"
                                    fieldState={
                                        theForm.formState.passwordconfirm
                                    }
                                />

                                <Button
                                    type="primary"
                                    onClick={handleResetPassword}
                                    disabled={!theForm.formValid || pending}
                                    className={cx(
                                        styles.reset__btn,
                                        styles.reset__btn__primary,
                                    )}
                                >
                                    <span
                                        className={styles.reset__button__label}
                                    >
                                        Save new password
                                    </span>
                                </Button>
                            </div>
                            <NavLink to="/login" className={styles.reset__link}>
                                Just remembered your password?{" "}
                                <span>Login</span>
                            </NavLink>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
